import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import { useFormik } from 'formik';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HorizontalRule } from '@mui/icons-material';
import * as Yup from 'yup';
import { Repeat } from 'react-feather';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';

// Utilities
import { useQueryParams } from 'src/hooks/useQueryParam';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { TypeAnalyticsPackageSearch } from 'src/utils/typings/dto';
import { TypeDataPackage } from 'src/utils/constants/enum';
import dayjs from 'dayjs';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import {
  PACKAGE_NAME_LIST,
  RankByDataPackage,
  RankByDataPackageValue,
} from 'src/features/AnalyticsPackage/Common/config';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import FormItemCountry from 'src/components/Form/FormItemCountry';
import IconFeather from 'src/components/common/Icon';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import ModalComponent from 'src/components/Modal/ModalComponent';

interface IProps {
  open: boolean;
  handleClose: () => void;
}

const FormSearchAnalyticsPackage: React.FC<IProps> = ({ open, handleClose }) => {
  const navigate = useNavigate();
  const {
    data: { data: runTimeConfig },
  } = useQueryRuntimeConfig();

  const params = useQueryParams();

  const [initTradeFrom, initTradeTo] = useMemo(() => {
    return [dayjs().subtract(1, 'month'), dayjs()];
  }, []);

  const getDefaultValue = (key: string) => {
    return get(params, key, '') as string;
  };
  const formik = useFormik<TypeAnalyticsPackageSearch>({
    enableReinitialize: true,
    initialValues: {
      type_package:
        (getDefaultValue('type_package') as TypeDataPackage) || TypeDataPackage.top_20_import_company_in_country,
      trade_data_from: getDefaultValue('trade_data_from')
        ? dayjs(getDefaultValue('trade_data_from'), FORMAT_DATE_TIME.MM_YYYY)
        : initTradeFrom,
      trade_data_to: getDefaultValue('trade_data_to')
        ? dayjs(getDefaultValue('trade_data_to'), FORMAT_DATE_TIME.MM_YYYY)
        : initTradeTo,
      rank_by: (getDefaultValue('rank_by') as RankByDataPackage) || RankByDataPackage.Weight,
      country: getDefaultValue('country') || '',
      import_country: getDefaultValue('import_country') || '',
      export_country: getDefaultValue('export_country') || '',
      is_worldwide: getDefaultValue('is_worldwide') == 'true',
    },
    validationSchema: Yup.object().shape({
      trade_data_from: Yup.mixed()
        .required('Trade Date From is required')
        .test('is-greater', 'Invalid date range', function (_, context) {
          const trade_data_from = dayjs(context.parent.trade_data_from || 'Invalid date');
          const trade_data_to = dayjs(context.parent.trade_data_to || 'Invalid date');
          if (!(trade_data_from.isValid() && trade_data_to.isValid())) return true;
          return trade_data_from.isBefore(trade_data_to);
        }),
      trade_data_to: Yup.mixed().required('Trade Date To is required'),
      import_country: Yup.string().test('type_contribute', 'This field is required', (text?: string) => {
        const typePackage = formik?.values?.type_package;
        if (typePackage === TypeDataPackage.top_20_trade_balance_between_2_countries && !text) {
          return false;
        }
        return true;
      }),
      export_country: Yup.string().test('type_contribute', 'This field is required', (text?: string) => {
        const typePackage = formik?.values?.type_package;
        if (typePackage === TypeDataPackage.top_20_trade_balance_between_2_countries && !text) {
          return false;
        }
        return true;
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      handleClose();
      setSubmitting(false);
      const params = handleQueryParamFromObject({
        ...values,
        trade_data_from: values.trade_data_from?.startOf('month').format(FORMAT_DATE_TIME.MM_YYYY),
        trade_data_to: values.trade_data_to?.endOf('month').format(FORMAT_DATE_TIME.MM_YYYY),
        is_worldwide: isEmpty(values?.country) ? true : values.is_worldwide,
      });
      navigate({ pathname: AuthRouteKeys.AnalyticsPackage, search: params });
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  const renderError = (id: string) => {
    return (
      get(formik.touched, id) &&
      get(formik.errors, id) && (
        <FormHelperText sx={{ marginLeft: 2 }} error id="standard-weight-helper-text-email-login">
          {get(formik.errors, id, '').toString()}
        </FormHelperText>
      )
    );
  };

  const renderCountrySelect = () => {
    switch (formik?.values?.type_package) {
      case TypeDataPackage.top_20_trade_balance_between_2_countries:
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            divider={
              <IconFeather
                icon={<Repeat />}
                muiProps={{
                  color: 'action',
                  fontSize: 'small',
                }}
              />
            }
          >
            <FormItemCountry id="import_country" label="Import Country" required={true} />
            <FormItemCountry id="export_country" label="Export Country" required={true} />
          </Stack>
        );
      default:
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormItemCountry id="country" label="Country" disabled={formik.values.is_worldwide} />
            <FormControlLabel
              value={formik.values.is_worldwide}
              key="is_worldwide"
              name="is_worldwide"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              control={<Checkbox checked={formik.values.is_worldwide} />}
              label={<Typography variant="body2">Worldwide</Typography>}
            />
          </Stack>
        );
    }
  };

  return (
    <ModalComponent open={open} handleClose={handleClose}>
      <FormikForm formik={formik}>
        <Stack spacing={3}>
          <Box textAlign="center">
            <Typography variant="h5">Analysis Data Package</Typography>
            <Typography color="text.secondary" variant="body2">
              Ready-made and quality packaged analysis data packages
            </Typography>
          </Box>
          <TextField
            value={formik.values['type_package']}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="type_package"
            name="type_package"
            size="small"
            placeholder="Enter"
            select
            label={
              <span>
                Data Package{' '}
                <TooltipFormLabel
                  title="Select the analytics solution to access pre-packaged data tailored for your analysis.
"
                />
              </span>
            }
          >
            {PACKAGE_NAME_LIST.map((type) => (
              <MenuItem value={type.value} key={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          {renderCountrySelect()}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            divider={<HorizontalRule color="action" fontSize="small" />}
          >
            <Box flex={1}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_data_from}
                onChange={(data) => {
                  formik.setFieldValue('trade_data_from', data);
                  formik.setFieldTouched('trade_data_from');
                }}
                label="Trade Data From"
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_data_from,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                  },
                }}
                views={['month', 'year']}
                minDate={dayjs(runTimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runTimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              />
              {renderError('trade_data_from')}
            </Box>
            <Box flex={1}>
              <DatePickerWithDefaultIcon
                disableFuture={true}
                value={formik.values.trade_data_to}
                onChange={(data) => {
                  formik.setFieldValue('trade_data_to', data);
                  formik.setFieldTouched('trade_data_to');
                }}
                label="To"
                slotProps={{
                  textField: {
                    error: !!formik.errors.trade_data_to,
                    size: 'small',
                    fullWidth: true,
                    required: true,
                  },
                }}
                views={['month', 'year']}
                minDate={dayjs(runTimeConfig?.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                maxDate={dayjs(runTimeConfig?.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
              />
              {renderError('trade_data_to')}
            </Box>
          </Stack>
          <FormControl>
            <FormLabel id="rank_by">
              <Typography variant="caption2">
                Rank By
                <TooltipFormLabel title="Choose a value (Weight, Quantity, Number of B/L, or USD Values) to sort and rank the data based on your preference." />
              </Typography>
            </FormLabel>
            <RadioGroup row aria-labelledby="rank_by" name="row-radio-buttons-group">
              {RankByDataPackageValue.map(({ label, value }) => {
                const checked = value === String(formik.values.rank_by);
                return (
                  <FormControlLabel
                    value={value}
                    key={value}
                    control={
                      <Radio
                        id={value}
                        name={value}
                        onChange={(e) => {
                          formik.setFieldValue('rank_by', e.target.name);
                        }}
                        onBlur={formik.handleBlur}
                        checked={checked}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption2">{label}</Typography>}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
          <ButtonWithLoading type="submit" variant="contained" isRounded sx={{ height: 50 }}>
            Filter Now
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};

export default FormSearchAnalyticsPackage;
